import { Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import React, { useReducer } from 'react'
import styled from 'styled-components'

import { GetCustomerCartQuery } from '../../../graphql/magento'
import { useResizeObserver } from '../../../hooks/useResizeObserver'
import { setCartFooterHeightFromBoxSize } from '../../../lib/cart/footer'
import { reduceCartState } from '../../../lib/cart/reducers/cart-state'
import { usePageUrl } from '../../../lib/pages'
import { CartFooterSpacer } from '../../cart/CartFooterSpacer'
import CartItemsSummary from '../summary/CartItemsSummary'
// Bringing in CartItemSummary to tweak its styles in this context only.
import CartItemSummary from '../summary/CartItemSummary'
import MiniCartModalFooter from './MiniCartModalFooter'
import MiniCartModalHeader from './MiniCartModalHeader'

const DialogTitleTypography = styled(Typography)(({ theme }) => ({
  color: theme.colors.grayscale.white,
  fontSize: '1rem',
  fontWeight: 'bold',
  margin: 0,
}))

const StyledCartItemsSummary = styled(CartItemsSummary)`
  header {
    height: auto;
    min-height: unset;
  }
`

export interface MiniCartModalProps {
  cart: NonNullable<GetCustomerCartQuery['customerCart']>
  className?: string
  disableButtons: boolean
  handleClose(): void
  open: boolean
}

const MiniCartModal: React.FC<MiniCartModalProps> = ({
  cart,
  className,
  disableButtons,
  handleClose,
  open,
}) => {
  const [{ codesCollapsed, summaryVisible }, dispatch] = useReducer(reduceCartState, {
    codesCollapsed: true,
    formDisabled: false,
    requestsPending: [],
    summaryVisible: false,
  })

  const resizeObserverRef = useResizeObserver(setCartFooterHeightFromBoxSize)

  const { navigate } = usePageUrl()
  const handleEditCart = () => {
    handleClose()
    navigate('/checkout')
  }

  return (
    <Dialog
      aria-describedby="scroll-dialog-description"
      aria-labelledby="scroll-dialog-title"
      className={className}
      fullWidth
      maxWidth="xs"
      onClose={handleClose}
      open={open}
      PaperProps={{ className: 'dialogPaper' }}
    >
      <DialogTitle className="dialogTitleRoot" disableTypography id="scroll-dialog-title">
        <DialogTitleTypography
          variant="h3"
          style={{
            fontSize: '1rem',
          }}
        >
          YOUR CART
        </DialogTitleTypography>
        <IconButton
          aria-label="close"
          className="closeButton"
          onClick={handleClose}
          disabled={disableButtons}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <MiniCartModalHeader
          cart={cart}
          disableButtons={disableButtons}
          handleEditCart={handleEditCart}
        />
        <StyledCartItemsSummary cart={cart} suppressHeader />
        <CartFooterSpacer
          // allow for applied promo toggle in fallback
          fallbackHeight={240}
        />
        <MiniCartModalFooter
          cart={cart}
          codesCollapsed={codesCollapsed}
          disableButtons={disableButtons}
          dispatch={dispatch}
          handleCloseCart={handleClose}
          ref={resizeObserverRef}
          summaryVisible={summaryVisible}
        />
      </DialogContent>
    </Dialog>
  )
}

const StyledMiniCartModal = styled(MiniCartModal)(({ theme }) => ({
  marginLeft: 'auto',
  maxWidth: '416px',
  [CartItemSummary]: {
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  '.closeButton': {
    color: theme.colors.grayscale.white,
    marginTop: '-4px',
    '&:hover': {
      backgroundColor: 'initial',
    },
  },
  '.dialogPaper': {
    borderRadius: '0',
    bottom: '0',
    left: '0',
    margin: '0',
    maxWidth: 'unset',
    minHeight: '100%',
    position: 'absolute',
    right: '0',
    top: '0',
    width: '100%',
  },
  '.dialogTitleRoot': {
    alignItems: 'center',
    display: 'flex',
    height: '55px',
    justifyContent: 'space-between',
    paddingRight: '0',
  },
  // Allow the product CartItemSummary components to be full width:
  '.MuiDialogContent-dividers': {
    padding: '0',
  },
  '@media(min-width: 960px)': {
    display: 'none',
  },
}))

export default StyledMiniCartModal
